import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import { DialogContent } from '@mui/material/'
import Stack from '@mui/material/Stack'
import { ReactComponent as CloseIcon } from '../../../assets/images/Close.svg'
import { SvgIcon, TextField } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'
import { ValueNarrative } from '../queries/useGetValueNarrativesQuery'
import { useArchiveValueNarrativeMutation } from '../queries/useArchiveValueNarrativeMutation'
import useValueNarrativeModal from './hooks/useValueNarrativeModal'

const ArchiveValueNarrativeModalContext = createContext<ReturnType<typeof useValueNarrativeModal> | undefined>(
    undefined
)

const ArchiveValueNarrativeModalProvider = ({ children }: PropsWithChildren<{}>) => {
    const archiveValueNarrativeModal = useValueNarrativeModal()

    return (
        <ArchiveValueNarrativeModalContext.Provider value={archiveValueNarrativeModal}>
            <ArchiveValueNarrativeModal />
            {children}
        </ArchiveValueNarrativeModalContext.Provider>
    )
}

export const useArchiveValueNarrativeModalContext = () => {
    const context = useContext(ArchiveValueNarrativeModalContext)

    if (!context)
        throw new Error('useArchiveValueNarrativeModalContext must be used within ArchiveValueNarrativeModalContext')

    const [valueNarrative, setValueNarrative] = useState<ValueNarrative | null>(null)

    useEffect(() => {
        if (context.valueNarrative) {
            setValueNarrative(context.valueNarrative)
        }
    }, [context.valueNarrative])

    return { ...context, valueNarrative }
}

const ArchiveValueNarrativeModal = () => {
    const { valueNarrative, isOpen, handleClose } = useArchiveValueNarrativeModalContext()

    const { mutateAsync, isLoading } = useArchiveValueNarrativeMutation({
        onArchiveValueNarrative: handleClose,
    })

    const [note, setNote] = useState<string | null>(null)

    useEffect(() => {
        setNote(null)
    }, [isOpen])

    const handleSave = async () => {
        await mutateAsync({ _id: valueNarrative._id, archived: { archivedNote: note || '' } })
    }

    const canSave = !isLoading

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            fullWidth
            aria-labelledby="form-dialog-title"
            maxWidth="sm"
            PaperProps={{
                sx: {
                    py: 4,
                    px: 8,
                    borderRadius: '24px',
                    boxShadow: '0px 15px 50px 0px rgba(0, 0, 0, 0.30)',
                },
            }}
        >
            <DialogTitle id="form-dialog-title" sx={{ p: 0 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    pb={1.5}
                    borderBottom="1px solid"
                    borderColor="figma.grayscale.stroke"
                >
                    <Typography variant="22x600" color="figma.grayscale.textHier1">
                        Archiving Value Narrative
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        sx={{
                            p: 0.5,
                            color: 'figma.grayscale.textHier2',
                        }}
                    >
                        <SvgIcon component={CloseIcon} inheritViewBox fontSize="medium" color="inherit" />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                <Stack direction="column" gap={3} py={3}>
                    <Stack direction="column" gap={1.5}>
                        <Typography variant="18x500" color="figma.grayscale.textHier1">
                            Add a note
                        </Typography>
                        <TextField
                            multiline
                            variant="outlined"
                            minRows={2}
                            maxRows={8}
                            placeholder="Enter your note here"
                            value={note}
                            onFocus={(event) => {
                                event.target.setAttribute('autocomplete', 'off')
                            }}
                            onChange={(e) => setNote(e.target.value)}
                            fullWidth
                            sx={{
                                '.MuiInputBase-root': {
                                    borderRadius: '12px',
                                },
                            }}
                        />
                    </Stack>
                    <Stack direction="column" gap={1.5}>
                        <Typography variant="16x400" color="figma.grayscale.textHier1">
                            Selected Value Narrative:
                        </Typography>
                        <Typography variant="16x400" color="figma.grayscale.textHier2">
                            {valueNarrative?.name}
                        </Typography>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Box flex={1} display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        onClick={handleClose}
                        // @ts-expect-error mui type
                        variant="outlined-sm-button"
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={!canSave}
                        onClick={handleSave}
                        // @ts-expect-error mui type
                        variant="contained-sm-button"
                        color="primary"
                        sx={{
                            '&.Mui-disabled': {
                                color: 'figma.primary2',
                            },
                        }}
                    >
                        Archive
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    )
}

export default ArchiveValueNarrativeModalProvider
